const connectLabels = {
  connectYourApps: 'Connect Your Apps',
  connectAppsSubtitle:
    'Integrate with third-party platforms to unlock more features.',
  socialmedia: 'Social Media',
  business: 'Business',
  facebookName: 'Facebook',
  facebookDescription: 'Connect your Facebook account to enable publishing.',
  twitterName: 'Twitter (X)',
  twitterDescription:
    'Connect your Twitter/X account via Late to enable publishing.',
  instagramName: 'Instagram',
  instagramDescription: 'Connect your Instagram account to enable publishing.',
  tiktokName: 'TikTok',
  tiktokDescription: 'Connect your TikTok account to enable publishing.',
  cloverName: 'Clover',
  cloverDescription: 'Connect your Clover POS for business integration.',
  connect: 'Connect',
  disconnect: 'Disconnect',
};
import { DataType } from '..';

const notFoundLabels = {
  emailUs: 'Email us at info@neberox.com to apply for a test account',
  waitlist: 'Join the waitlist',
};

const genericLabels = {
  company: 'Syntraxis',
  contactUs: 'Contact Us',
  logout: 'Logout',
  month: 'Month',
  na: 'N/A',
  add: '+ Add',
  cancel: 'Cancel',
  save: 'Save',
  terms: 'Terms',
  privacy: 'Privacy Policy',
  reports: 'Reports',
  and: 'and',
  delete: 'Delete',
};

const loginLabels = {
  signIn: 'Sign in',
  detail: 'With your business google account',
  new: 'New to Syntraxis?',
};

const dashboardLabels = {
  keywords: 'Keywords',
  toDo: 'To-Do List',
  reviews: 'Reviews',
  businessLocation: 'Business Location',
};

const keywordLabels = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  delete: 'Delete Keyword',
  performanceStats: 'Performance Statistics',
  performanceStatsInfo:
    'Track key engagement metrics over time (calls, website visits, directions, impressions) to understand marketing impact.',
  typeKeyword: 'Type Keyword',
  overallStrength: 'Over all profile strength',
  reviewStats: 'Review Statistics',
  reviewStatsInfo:
    'Summarized review sentiment and counts to help you identify trends at a glance and spot areas needing attention.',
  rankings: 'Keywords Rankings',
  mapTitle: 'View Keyword Ranking Map',
  description:
    'Keywords for local SEO for a place can vary depending on the specific location, offerings, and target audience. These are some of the keywords found relevant to your business. Keeping track of the progress is key in our system 👍',
  processingKeywords: 'Processing Keywords',
  researchKeywords:
    'The keywords for your businesses are still being researched by the simiplicty team',
  info: 'Track keyword performance, review statistics, and overall profile strength. Use these insights to prioritize SEO efforts and measure progress over time.',
};

const statsLabels = {
  movementInfo: 'places (since keyword was added)',
  reviewStatsInfo: 'Reviews (since first login)',
  keywordStatsInfo: 'Keywords (since keywords were added)',
  wordStatsInfo: 'Words (since first login)',
  competitorsText: (count: DataType) => `Out of ${count} local competitors`,
  placesRankingText: (ranking: DataType) => `${ranking} places`,
};

const reportLabels = {
  searchPlaces: 'Search Places',
  typePlace: 'Type Place name',
  description: 'Generate Reply',
  addKeywords: 'Add Keywords',
  generate: 'Generate Report',
  selectRadius: 'Select radius',
  info: 'Generate a quick competitive snapshot for a location. Provide a place, radius, and keywords to see relative strength and competitor averages.',
};

const reviewLabels = {
  title: 'Reply Recent Reviews',
  stars: 'Stars',
  generateReply: 'Generate Reply',
  suggestedReply: 'Suggested Reply',
  postReply: 'Post Reply',
  noReviewsTitle: 'You’re all caught up!',
  resetReviews: 'Fetch Reviews',
  noReviewsSubtitle: 'Well done replying to all of your customers reviews!',
  info: 'View and manage your recent Google reviews. Toggle the switch to focus on unreplied items. Click a review to open details, generate an AI-suggested reply, and post it.',
};

const todoLabels = {
  edit: 'Edit item',
  delete: 'Delete item',
  progress: 'Your checklist progress',
  essentialInfo: 'Essential Information',
  mustHaves: 'Syntraxis Must Haves',
  addChecklist: 'Type checklist item',
};

const alertLabels = {
  error: 'An unknown error has occurred',
  signout: 'Are you sure you want to sign out?',
  deleteToDo: (title: DataType) =>
    `Are you sure, you want to delete "${title}"`,
  deleteKeyword: (keyword: DataType) =>
    `Are you sure, you want to delete ${keyword} keyword`,
};

const chartLabels = {
  date: 'Date',
  count: 'Count',
  select: 'Select a type',
  selectKeyword: 'Select keyword',
  callClicks: 'Call Clicks',
  callsText: 'Calls made from your business profile',
  websiteClicks: 'Website Clicks',
  websiteText: 'Website opened from your business profile',
  businessDirections: 'Business Directions',
  mobileImpressions: 'Mobile Impressions',
  businessDirectionsText: 'Directions opened from your business profile',
  mobileImpressionsText: 'Mobile impressions made from your business profile',
};

const settingsLabels = {
  title: 'Review reply guidelines',
  placeholder:
    "Write any guidelines for how replies should be phrased (tone, do/don'ts, etc.)",
  saving: 'Saving…',
  autoReplyLabel: 'Auto-reply to new reviews',
  info: 'Set the tone and rules for AI-generated replies. Enable auto-reply to automatically respond when new reviews arrive.',
  qrTitle: 'Google Review QR Code',
  qrInfo:
    "Scan or print this code to link directly to your business's review or maps page.",
  qrDownload: 'Download PDF',
  qrAlt: 'Google review QR code',
  reviewLandingLabel: 'Review landing message',
  reviewLandingPlaceholder: 'Review and get 20% discount',
  checkinsBrandingTitle: 'Email branding & offer',
  checkinsBrandingInfo:
    'Upload your logo and set the offer details that will be merged into the welcome email guests receive after check-in.',
  checkinsEmailLogoLabel: 'Logo',
  checkinsEmailLogoHelp: 'Shown at the top of the welcome email.',
  checkinsOfferPercentLabel: 'Offer (% off)',
  checkinsOfferPercentPlaceholder: '20',
  checkinsOfferValidityLabel: 'Offer validity (days)',
  checkinsOfferValidityPlaceholder: 'Valid for 14 days after receipt',
  checkinsOfferCodeLabel: 'Redemption code',
  checkinsOfferCodePlaceholder: 'WELCOME20',
  checkinsOfferDetailsLabel: 'Fine print / details',
  checkinsOfferDetailsPlaceholder: 'One-time use, in-store only.',
  uploadLogoCta: 'Upload logo',
  logoUploading: 'Uploading…',
  logoPlaceholder: 'Logo',
  logoUploadFailed: 'Unable to upload logo right now.',
  logoLocationRequired: 'Pick a location before uploading a logo.',
  checkinsTitle: 'QR check-ins',
  checkinsSubtitle:
    'People who scanned your QR and filled out the form show up here.',
  checkinsSearchPlaceholder: 'Search name or email',
  checkinsRangeLabel: 'Time range',
  checkinsRange7d: 'Last 7 days',
  checkinsRange30d: 'Last 30 days',
  checkinsRangeAll: 'All time',
  checkinsOptInOnly: 'Opted-in only',
  checkinsStatTotal: 'Total check-ins',
  checkinsStatOptIn: 'Opted-in',
  checkinsStatRecent: 'This week',
  checkinsSelectLocation: 'Pick a location to see QR check-ins.',
  checkinsEmptyTitle: 'No check-ins yet',
  checkinsEmptySubtitle:
    'As soon as guests scan and submit the form, they will appear here.',
  checkinsUnknownVisitor: 'Visitor',
  checkinsOptInChip: 'Opted in',
  checkinsNoOptInChip: 'No opt-in',
  checkinsSendOne: 'Send promo',
  checkinsSendCta: 'Send promotion',
  checkinsRefresh: 'Refresh',
  checkinsPromoTitle: 'Send promotion',
  checkinsPromoBody: (count: DataType) =>
    `This will go to ${count} contact(s) in your filtered list.`,
  checkinsPromoSubject: 'Subject',
  checkinsPromoMessage: 'Message',
  checkinsPromoSend: 'Send',
  checkinsPromoSending: 'Sending…',
  checkinsPromoCancel: 'Cancel',
  checkinsPromoSuccess: (count: DataType) =>
    `Promotion queued for ${count} contact(s).`,
  checkinsPromoFailed: 'Unable to send promotion right now.',
  checkinsPromoDefaultSubject: 'VIP offer for recent visitors',
  checkinsPromoDefaultMessage:
    'Thanks for checking in with us! Here is a thank-you offer for your next visit.',
  checkinsWelcomeTitle: 'Check-in welcome email',
  checkinsWelcomeInfo:
    'Default email sent automatically to guests who opt in at check-in.',
  checkinsWelcomeSubjectLabel: 'Welcome email subject',
  checkinsWelcomeSubjectPlaceholder: 'Thanks for checking in!',
  checkinsWelcomeMessageLabel: 'Welcome email message',
  checkinsWelcomeMessagePlaceholder:
    'Hi there! Thanks for checking in with us. Here is an offer for your next visit.',
};

const billingLabels = {
  locationsBillingTitle: 'Location subscriptions',
  locationsBillingSubtitle:
    'Activate per-location subscriptions. New subscriptions include a 7-day free trial.',
  noLocations:
    'No locations found. Connect your Google account to see locations.',
  columnLocation: 'Location',
  columnStatus: 'Status',
  columnActions: 'Actions',
  statusNoSubscription: 'No subscription',
  statusTrial: 'Trial',
  statusTrialExpired: 'Trial expired',
  statusActive: 'Active',
  activateWithTrial: 'Activate subscription',
  billingCycleMonthly: 'Monthly',
  billingCycleYearly: 'Annual',
  planPopular: 'Popular',
  plans: {
    essential: {
      name: 'Essentials',
      cta: 'Get Started',
      description:
        'Foundation plan to protect your reputation and understand how new customers discover you.',
      features: [
        'Live feed of new Google reviews across your locations',
        'Smart reply suggestions that keep your tone consistent',
        'Search keyword insights showing which terms drive visits',
        'Simple reports your team can act on every week',
      ],
      prices: {
        monthly: { display: '£29', frequency: 'Per month' },
        yearly: { display: '£299', frequency: 'Per year' },
      },
    },
    growth: {
      name: 'Growth',
      cta: 'Get Started',
      description:
        'Best for growing brands that want one place to manage reviews and social content together.',
      features: [
        'Everything in Reviews & Keywords',
        'Schedule social posts across major channels',
        'Central inbox for comments, DMs, and mentions',
        'Post-level analytics on reach, clicks, and engagement',
        'Best-time-to-post suggestions based on past performance',
      ],
      prices: {
        monthly: { display: '£59', frequency: 'Per month' },
        yearly: { display: '£599', frequency: 'Per year' },
      },
    },
    elite: {
      name: 'Elite',
      cta: 'Get Started',
      description:
        'Everything you need to run multi-location reputation, social, and automation at scale.',
      features: [
        'Everything in Growth',
        'Automated AI workflows for campaigns and follow-ups',
        'Executive dashboards for locations in real time',
        'Role-based permissions, audit trails, and SSO options',
        'Dedicated onboarding and strategic success guidance and dedicated support',
      ],
      prices: {
        monthly: { display: '£149', frequency: 'Per month' },
        yearly: { display: '£1,499', frequency: 'Per year' },
      },
    },
  },
};

const expenseLabels = {
  title: 'Expense tracker',
  addExpenseTitle: 'Add an expense',
  recentExpensesTitle: 'Recent expenses',
  recentExpensesHelper: 'Inline delete to remove. More filters coming soon.',
  formDescriptionLabel: 'Description',
  formDescriptionPlaceholder: 'e.g., Coffee beans, Meta ads, Staff meal',
  formCategoryLabel: 'Category',
  formAmountLabel: 'Amount',
  formPaymentLabel: 'Payment',
  formAddButton: 'Add',
  listEmpty: 'No expenses yet. Add your first one to track spend.',
  paymentFallback: 'Payment',
  totalSpend: 'Total spend',
  totalSpendDescription: 'Sum of all tracked expenses',
  byCategory: 'By category',
  byCategoryPlaceholder: 'Add expenses to see category totals.',
  aiOverline: 'Syntraxis AI Control Hub',
  aiHeadline: 'Scan receipts and let AI keep your spend tidy',
  aiDescription:
    "Capture a receipt photo and we'll auto-create the expense with category, amount, and a timestamped memo. Works great for walk-around ops, supply runs, and team reimbursements.",
  chipReadReceipts: 'Read receipts',
  chipPosMatching: 'POS + card matching',
  chipRouteBudgets: 'Route to budgets',
  chipAuditExport: 'Audit-ready export',
  captureButton: 'Capture or upload receipt',
  reconcileButton: 'Ask AI to reconcile',
  receiptFallbackName: 'Receipt',
  receiptMemo: (vendor: DataType) => `${vendor} (receipt)`,
  receiptToast: (detail: DataType) => `Added ${detail} from receipt.`,
  categories: {
    Ops: 'Ops',
    Marketing: 'Marketing',
    Staff: 'Staff',
    Inventory: 'Inventory',
    Other: 'Other',
  },
};

const reviewLandingLabels = {
  fallbackMessage: 'Review and get 20% discount',
  headingInfo: 'We’ll direct you to Google Reviews after a quick check-in.',
  fieldName: 'Name',
  fieldEmail: 'Email',
  optIn: 'Email me my review link and occasional updates.',
  continue: 'Continue to Google Reviews',
  termsPrefix: 'By continuing you agree to our',
  missingUrl: 'Missing review URL.',
  poweredBy: 'Powered by',
};

const campaignLabels = {
  title: 'Social Media Post Agent',
  platform: 'Platform',
  platformPlaceholder: 'Select platform',
  sourceMode: 'Input Source',
  modeUrl: 'URL',
  modeText: 'Free Text',
  sourceUrl: 'Source URL',
  freeText: 'Post Ideas / Draft',
  hashtagsLimit: 'Hashtags Limit',
  guidelines: 'Brand Guidelines',
  desiredTone: 'Desired Tone',
  toneFriendly: 'Friendly',
  toneProfessional: 'Professional',
  toneWitty: 'Witty',
  toneFormal: 'Formal',
  allowEmoji: 'Allow Emoji',
  mediaOption: 'Media Source',
  mediaNone: 'None',
  mediaAi: 'AI Generated',
  mediaUpload: 'Upload',
  mediaUrl: 'From URL',
  imageUrl: 'Image / Video URL',
  uploadFile: 'Upload File',
  approxChars: 'Approximate Character Count',
  history: 'History',
  output: 'Output',
  schedule: 'Schedule',
  run: 'Run',
  info: 'Configure and run a social media post generation agent. Provide a source URL, optional image, and target length. Review output, history, and schedule automation.',
  historyEmpty:
    'Every time you click run a history item will appear here ordered by the time it was created.',
  outputPlaceholder: 'Generated post output will appear here.',
  schedulePlaceholder: 'Configure recurring generations (coming soon).',
  tabsAria: 'Campaigns tabs',
  aboutAria: 'About campaigns',
  ideasPlaceholder: 'Write ideas or paste text...',
  guidelinesPlaceholder: 'Defaults from Settings will apply',
  uploadButton: 'Upload File',
  generatePost: 'Generate Post',
  generating: 'Generating…',
};

const assistantLabels = {
  promptTitle: 'Try a prompt',
  exampleTitle: 'Live example',
  exampleBody:
    'Morning! Here’s the pulse: Toast + Square combined revenue is up 7% WoW, but Location B saw a 12% drop after 6pm. Google rating slipped to 4.1 due to two unanswered reviews. Want me to draft replies and schedule an Instagram story to recover evening traffic?',
  disclaimerPrefix:
    'Syntraxis can make mistakes. Check important info. See our',
  termsLink: 'Terms',
  thinking: 'Thinking…',
  errorMessage: 'Unable to reach the assistant right now.',
  dataTitle: 'Structured results',
  prompts: [
    {
      text: 'Summarize yesterday’s sales and footfall across Toast and Square',
      category: 'insights',
    },
    {
      text: 'Draft a weekend promo for Instagram + email using our brand tone',
      category: 'marketing',
    },
    {
      text: 'Flag locations with slipping Google ratings and suggest replies',
      category: 'reviews',
    },
    {
      text: 'Find SKUs trending down and recommend bundles to recover',
      category: 'inventory',
    },
    {
      text: 'Highlight top-performing locations and why they outpaced the average',
      category: 'insights',
    },
    {
      text: 'Create a one-click follow-up SMS for yesterday’s high spenders',
      category: 'marketing',
    },
    {
      text: 'Detect abnormal refunds or voids in the last 24 hours',
      category: 'risk',
    },
    {
      text: 'Draft replies for 1-star reviews using our guidelines',
      category: 'reviews',
    },
    {
      text: 'Build a daily briefing for the GM with KPIs and alerts',
      category: 'ops',
    },
    {
      text: 'Prepare a post for Facebook + IG that matches our brand tone',
      category: 'marketing',
    },
  ],
};
export {
  chartLabels,
  alertLabels,
  reviewLabels,
  reportLabels,
  statsLabels,
  notFoundLabels,
  genericLabels,
  loginLabels,
  dashboardLabels,
  keywordLabels,
  todoLabels,
  settingsLabels,
  campaignLabels,
  reviewLandingLabels,
  connectLabels,
  billingLabels,
  expenseLabels,
  assistantLabels,
};
